import type PrinterModel from 'garaje/models/abstract/printer';

/**
 * Reachability information for a printer, from the dashboard's perspective.
 *
 * - `isReachable` — true when this printer will accept a print job via either
 *   the primary path (an iPad has it as primary and reports connected) or the
 *   failover path (an iPad has it as failover and reports failoverPrinterStatus
 *   connected).
 * - `primaryDeviceCount` — iPads where this printer is the primary.
 * - `failoverDeviceCount` — iPads anywhere in `allPrinters` whose
 *   `failoverPrinter` points at this one. Counted regardless of freshness; this
 *   is a pure association count, not a liveness signal.
 * - `reachableViaFailover` — true only when the primary path is *not* live but
 *   the printer is still reachable via some iPad's failover. Use this flag to
 *   drive UI that wants to show "Operational via failover" distinctly from
 *   plain "Operational".
 *
 * Known callers: `getPrinterReachability` is intended for any caller rendering
 * printer status UI (e.g. `PrinterInfo::ConnectionStatus` callers: the Printers
 * list, global-devices Printers list, invite printer-selector).
 */
export type PrinterReachability = {
  isReachable: boolean;
  primaryDeviceCount: number;
  failoverDeviceCount: number;
  reachableViaFailover: boolean;
};

/**
 * Computes reachability for a single printer given the full set of printers at
 * the location. Needs `allPrinters` because the inverse relationship for
 * "iPads that have me as failover" isn't declared on the Printer model, so we
 * walk siblings' `devices` arrays.
 *
 * `devices.failover-printer` must be sideloaded in the caller's model hook so
 * `device.belongsTo('failoverPrinter').id()` resolves.
 */
export function getPrinterReachability(printer: PrinterModel, allPrinters: PrinterModel[]): PrinterReachability {
  const hasPrimaryPath = printer.hasConnectedPath;
  const primaryDeviceCount = printer.listableDevices?.length ?? 0;

  let failoverDeviceCount = 0;
  let failoverPathLive = false;
  allPrinters.forEach((other) => {
    (other.listableDevices ?? []).forEach((device) => {
      if (device.belongsTo('failoverPrinter').id() !== printer.id) return;
      failoverDeviceCount += 1;
      if (
        !failoverPathLive &&
        device.isIpadSeenRecently &&
        device.failoverPrinterStatus?.toLowerCase() === 'connected'
      ) {
        failoverPathLive = true;
      }
    });
  });

  const isReachable = hasPrimaryPath || failoverPathLive;
  const reachableViaFailover = isReachable && !hasPrimaryPath;

  return { isReachable, primaryDeviceCount, failoverDeviceCount, reachableViaFailover };
}

/**
 * Thin wrapper around `getPrinterReachability` that returns only the boolean
 * reachability signal. Preserved for existing callers (`new-visitor-form`,
 * `reprint-badge-modal`).
 */
export function isPrinterReachable(printer: PrinterModel, allPrinters: PrinterModel[]): boolean {
  return getPrinterReachability(printer, allPrinters).isReachable;
}
